import { createServerFn } from "@tanstack/react-start";

// US CPI (BLS, CPI-U, not seasonally adjusted index) with gold monthly
// returns aligned to the release month. Public data, short cache, last good
// response retained when the source is unavailable.

export type CpiMonth = {
  period: string; // YYYY-MM reference month
  headlineYoY: number | null;
  coreYoY: number | null;
  headlineMoM: number | null;
  coreMoM: number | null;
  goldReleaseMonthPct: number | null; // gold return in the month the print was released
};

export type CpiDTO = { months: CpiMonth[]; error?: string; stale?: boolean };

const BLS_URL = "https://api.bls.gov/publicAPI/v2/timeseries/data/";
const HEADLINE = "CUUR0000SA0";
const CORE = "CUUR0000SA0L1E";
const TTL_MS = 10 * 60 * 1000;
let cache: { at: number; data: CpiDTO } | null = null;

type Datum = { year: string; period: string; value: string };

const pct = (a: number, b: number) => Math.round(((a - b) / b) * 1000) / 10;

export const getUsCpi = createServerFn({ method: "GET" }).handler(async (): Promise<CpiDTO> => {
  if (cache && Date.now() - cache.at < TTL_MS) return cache.data;
  try {
    const { blsFetch } = await import("@/lib/bls-fetch.server");
    const json = await blsFetch([HEADLINE, CORE], 6, 2);

    const toMap = (id: string) => {
      const m = new Map<string, number>();
      for (const d of json.Results?.series?.find((s) => s.seriesID === id)?.data ?? []) {
        if (!/^M(0[1-9]|1[0-2])$/.test(d.period)) continue;
        const v = Number(d.value);
        if (Number.isFinite(v)) m.set(`${d.year}-${d.period.slice(1)}`, v);
      }
      return m;
    };
    const h = toMap(HEADLINE);
    const c = toMap(CORE);
    if (h.size < 13) throw new Error("Insufficient CPI history");

    const shift = (p: string, by: number) => {
      const [y, m] = p.split("-").map(Number);
      const d = new Date(Date.UTC(y, m - 1 + by, 1));
      return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
    };

    const gold = new Map<string, number>();
    try {
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      const { data } = await supabaseAdmin.from("gold_monthly_history").select("year,month,open,close").limit(1000);
      for (const r of data ?? []) {
        if (r.open > 0) gold.set(`${r.year}-${String(r.month).padStart(2, "0")}`, pct(r.close, r.open));
      }
    } catch (e) {
      console.error("[cpi] gold history unavailable", e);
    }

    const months: CpiMonth[] = [...h.keys()]
      .sort()
      .map((p) => {
        const yo = shift(p, -12);
        const mo = shift(p, -1);
        const f = (m: Map<string, number>, base: string) => {
          const a = m.get(p);
          const b = m.get(base);
          return a != null && b != null && b !== 0 ? pct(a, b) : null;
        };
        return {
          period: p,
          headlineYoY: f(h, yo),
          coreYoY: f(c, yo),
          headlineMoM: f(h, mo),
          coreMoM: f(c, mo),
          goldReleaseMonthPct: gold.get(shift(p, 1)) ?? null,
        };
      })
      .filter((m) => m.headlineYoY != null);

    const data: CpiDTO = { months };
    cache = { at: Date.now(), data };
    const { saveSnapshot } = await import("@/lib/macro-snapshot.server");
    await saveSnapshot("cpi", data);
    return data;
  } catch (err) {
    const { isBlsGated } = await import("@/lib/bls-fetch.server");
    const gated = isBlsGated(err);
    if (!gated) console.error("[cpi] fetch failed", err);
    if (cache) {
      if (gated) {
        cache = { at: Date.now(), data: cache.data };
        return cache.data;
      }
      return { ...cache.data, stale: true };
    }
    const { loadSnapshot } = await import("@/lib/macro-snapshot.server");
    const snap = await loadSnapshot<CpiDTO>("cpi");
    if (snap?.months?.length) {
      cache = { at: gated ? Date.now() : Date.now() - TTL_MS + 10 * 60 * 1000, data: snap };
      return gated ? snap : { ...snap, stale: true };
    }
    return { months: [], error: "Inflation data is temporarily unavailable." };
  }
});
